<script setup>
  import { ref, computed, watch, onMounted, onUnmounted, nextTick, Teleport } from 'vue'
  import { RequestService } from '../services/RequestService'
  const props = defineProps({
    dropdownOptions: {
      type: Array,
      required: true
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Please select'
    },
    context: {
      type: Object,
      default: () => ({})
    },
    teleport: {
      type: Boolean,
      default: false
    },
    /** Full-width control with readable label (agency pickers, etc.). */
    wide: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  })

  watch(
    () => props.modelValue,
    (newValue) => {
      selectedOption.value = newValue
    }
  )

  const emit = defineEmits(['update:modelValue', 'change'])

  const isOpen = ref(false)
  const selectedOption = ref(props.modelValue)
  const dropdownRef = ref(null)
  const dropdownMenuRef = ref(null)
  const openUpwards = ref(false)
  const menuPosition = ref({ top: 'auto', bottom: 'auto', left: 0, width: 0 })
  const { handleErrors } = RequestService()

  const displayText = computed(() => {
    if (props.context.showCustom && props.context.customReminder !== null) {
      return `Custom: ${props.context.customReminder} days before`
    }
    const option = props.dropdownOptions.find((opt) => opt.value === selectedOption.value)
    return option ? option.text : props.placeholder
  })

  const isDropdownDisabled = computed(() =>
    props.disabled || props.loading
  )

  watch(isDropdownDisabled, (disabled) => {
    if (disabled) isOpen.value = false
  })

  const toggleDropdown = () => {
    if (isDropdownDisabled.value) return
    isOpen.value = !isOpen.value
    if (isOpen.value) {
      nextTick(() => {
        updateDropdownPosition()
        if (props.teleport) {
          updateTeleportPosition()
        }
      })
    }
  }

  const updateTeleportPosition = () => {
    const dropdownElement = dropdownRef.value
    if (!dropdownElement) return

    const rect = dropdownElement.getBoundingClientRect()
    const estimatedMenuHeight = 200 // Estimated max height for space calculation
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    openUpwards.value = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow

    if (openUpwards.value) {
      // Open upwards: position bottom edge at button's top
      menuPosition.value = {
        left: rect.left,
        width: rect.width,
        top: 'auto',
        bottom: window.innerHeight - rect.top
      }
    } else {
      menuPosition.value = {
        left: rect.left,
        width: rect.width,
        top: rect.bottom,
        bottom: 'auto'
      }
    }
  }

  const updateDropdownPosition = () => {
    const dropdownElement = dropdownRef.value
    const dropdownMenuElement = dropdownMenuRef.value
    if (!dropdownElement || !dropdownMenuElement) return

    const tableContainer = dropdownElement.closest('.table-container-events.card')
    if (!tableContainer) return

    const dropdownRect = dropdownElement.getBoundingClientRect()
    const containerRect = tableContainer.getBoundingClientRect()
    const spaceBelow = containerRect.bottom - dropdownRect.bottom
    const spaceAbove = dropdownRect.top - containerRect.top
    const dropdownHeight = dropdownMenuElement.offsetHeight
    openUpwards.value = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
  }

  const selectOption = (option) => {
    selectedOption.value = option.value
    emit('update:modelValue', option.value)
    emit('change', option)
    isOpen.value = false
  }

  const isOptionDisabled = (option) => {
    if (typeof option.isDisabled === 'function') {
      try {
        return option.isDisabled(props.context)
      } catch (error) {
        handleErrors(('Error in isDisabled function:', error))
        return false
      }
    }
    return false
  }

  const handleClickOutside = (event) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
      isOpen.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    window.addEventListener('resize', updateDropdownPosition)
    window.addEventListener('scroll', handleScroll, true)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('resize', updateDropdownPosition)
    window.removeEventListener('scroll', handleScroll, true)
  })

  const handleScroll = () => {
    if (isOpen.value && props.teleport) {
      updateTeleportPosition()
    }
  }
</script>

<template>
  <div class="dropdown" :class="{ 'dropdown--wide': wide }" ref="dropdownRef">
    <button
      type="button"
      class="btn btn-outline-secondary custom-btn"
      :class="[wide ? 'dropdown-btn-wide' : 'fixed-width', { show: isOpen }]"
      :disabled="isDropdownDisabled"
      :aria-disabled="isDropdownDisabled"
      @click="toggleDropdown">
      <span
        :class="[
          'text-16',
          'placeholder-dropdown',
          { selected: selectedOption },
          wide ? 'dropdown-label-wide' : 'text-truncate'
        ]"
      >
        {{ displayText }}
      </span>
      <span v-if="loading" class="spinner-border spinner-border-sm text-primary ms-2" role="status"></span>
      <i
        v-else
        :class="[
          'bi',
          'ms-2',
          'text-primary',
          isOpen ? 'bi-caret-up-fill' : 'bi-caret-down-fill'
        ]"></i>
    </button>
    <ul
      v-if="!teleport"
      ref="dropdownMenuRef"
      class="dropdown-menu"
      :class="[
        wide ? 'dropdown-menu-wide' : 'fixed-width',
        { show: isOpen, 'dropdown-menu-up': openUpwards }
      ]"
      :style="{ bottom: openUpwards ? '100%' : 'auto', top: openUpwards ? 'auto' : '100%' }">
      <li v-for="option in props.dropdownOptions" :key="option.value">
        <a
          class="dropdown-item hand-cursor"
          :class="[
            { disabled: isOptionDisabled(option), active: option.value === selectedOption },
            wide ? 'dropdown-item-wide' : 'text-nowrap'
          ]"
          @click="selectOption(option)">
          {{ option.text }}
        </a>
      </li>
    </ul>

    <!-- Teleported dropdown menu (renders outside container) -->
    <Teleport to="body" v-if="teleport">
      <ul
        ref="dropdownMenuRef"
        class="dropdown-menu dropdown-teleported"
        :class="[
          wide ? 'dropdown-menu-wide' : 'fixed-width',
          { show: isOpen, 'dropdown-menu-up': openUpwards }
        ]"
        :style="{
          position: 'fixed',
          top: menuPosition.top === 'auto' ? 'auto' : menuPosition.top + 'px',
          bottom: menuPosition.bottom === 'auto' ? 'auto' : menuPosition.bottom + 'px',
          left: menuPosition.left + 'px',
          width: menuPosition.width + 'px'
        }">
        <li v-for="option in props.dropdownOptions" :key="option.value">
          <a
            class="dropdown-item hand-cursor"
            :class="[
              { disabled: isOptionDisabled(option), active: option.value === selectedOption },
              wide ? 'dropdown-item-wide' : 'text-nowrap'
            ]"
            @click="selectOption(option)">
            {{ option.text }}
          </a>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
  .placeholder-dropdown,
  .disabled {
    color: var(--bs-gray-600) !important;
  }

  .btn-outline-secondary.btn.show {
    background: transparent !important;
  }

  .fixed-width {
    width: 195px !important;
  }

  .dropdown--wide {
    width: 100%;
    max-width: 100%;
  }

  .dropdown-btn-wide {
    width: 100% !important;
    max-width: 100%;
    min-height: 2.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .dropdown-label-wide {
    flex: 1;
    min-width: 0;
    text-align: left;
    white-space: normal;
    line-height: 1.35;
    word-break: break-word;
  }

  .dropdown-menu-wide {
    width: 100% !important;
    max-width: 100%;
    max-height: 320px;
  }

  .dropdown-item-wide {
    white-space: normal;
    line-height: 1.35;
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
  }

  .custom-btn {
    transition: box-shadow 0.3s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 0.5rem;

    &:hover {
      background-color: transparent;
      color: var(--bs-secondary);
      border-color: var(--bs-secondary);
    }

    &:active,
    &:focus,
    &.show {
      background-color: transparent !important;
      color: var(--bs-secondary) !important;
      border-color: #5c0cb8 !important;
      box-shadow: 0 0 0 0.25rem #cac0ff !important;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  .text-truncate {
    max-width: 150px;
    overflow: hidden;
    text-overflow: clip;
    white-space: nowrap;
  }

  .dropdown-menu {
    min-width: unset;
    padding: 0.5rem 0;
    position: absolute;
    max-height: 195px;
    overflow-y: auto;

    &.dropdown-menu-up {
      top: auto;
      bottom: 100%;
      margin-top: 0;
      margin-bottom: 0.125rem;
    }

    .dropdown-item {
      padding: 0.25rem 1rem;
      white-space: normal;
      word-wrap: break-word;

      &:hover {
        background-color: var(--gray-200);
      }
      &.active {
        background-color: var(--bs-primary) !important;
        color: #fff !important;
      }
    }
  }
</style>

<!-- Global styles for teleported dropdown (outside scoped context) -->
<style lang="scss">
  .dropdown-teleported {
    min-width: unset;
    padding: 0.5rem 0;
    max-height: 195px;
    overflow-y: auto;
    z-index: 1050;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

    &.dropdown-menu-up {
      margin-bottom: 0.125rem;
    }

    .dropdown-item {
      padding: 0.25rem 1rem;
      white-space: normal;
      word-wrap: break-word;
      cursor: pointer;

      &:hover {
        background-color: var(--gray-200);
      }
      &.active {
        background-color: var(--bs-primary) !important;
        color: #fff !important;
      }

      &.disabled {
        color: var(--bs-gray-600) !important;
        pointer-events: none;
      }
    }
  }
</style>
